import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { useThemeContext } from '../../contexts/ThemeContext';
import { PrivatePayrollAuctionLoading } from '../customTheme/PrivatePayrollAuction/Loading';
import { PoweredByQITech } from '../customTheme/PrivatePayrollAuction/components/PoweredByQITech';
import { ThemedButton } from '../../components/ThemedButton';
import { PrivatePayrollAuctionTheme } from '../customTheme';
import { ConfiaPage } from '../ConfiaPage';
import QISignTheme from '../../interfaces/Theme';
import * as S from './styles';

const COLLECTION_STEPS = ['face_recognition', 'personal_document', 'authentication_code'] as const;
type CollectionStep = typeof COLLECTION_STEPS[number];

const isCollectionStep = (step: string): step is CollectionStep =>
    (COLLECTION_STEPS as readonly string[]).includes(step);

interface EnvelopeFolderCollectionProps {
    folderSessionToken: string | undefined;
    resolvedTheme: QISignTheme;
    listing: any;
    initialStep: CollectionStep;
    buildAuthHeaders: (token: string) => { headers: { Authorization: string } };
    refreshListing: () => Promise<any>;
    setSessionExpired: () => void;
    onCollectionComplete: () => void;
    setSignMetadata?: (metadata: any) => void;
}

export const EnvelopeFolderCollection = (props: EnvelopeFolderCollectionProps) => {
    const {
        folderSessionToken,
        resolvedTheme,
        listing,
        initialStep,
        buildAuthHeaders,
        refreshListing,
        setSessionExpired,
        onCollectionComplete,
        setSignMetadata,
    } = props;

    const { clientThemeSettings } = useThemeContext();
    const theme: QISignTheme = clientThemeSettings ?? resolvedTheme;

    const [currentStep, setCurrentStep] = useState<CollectionStep>(initialStep);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [faceImageKey, setFaceImageKey] = useState<string | undefined>(undefined);
    const [confiaSignUrl, setConfiaSignUrl] = useState<string | undefined>(() =>
        listing?.provider === 'confia' ? listing?.confia_sign_url : undefined,
    );
    const [faceSdkReady, setFaceSdkReady] = useState<boolean>(
        () => typeof (window as any).ZaigWebFaceRecon !== 'undefined',
    );

    useEffect(() => {
        if (currentStep !== 'face_recognition') return;
        if (confiaSignUrl) return;
        if (setSignMetadata) setSignMetadata({ face: true });
        if (faceSdkReady) return;
        const id = window.setInterval(() => {
            if (typeof (window as any).ZaigWebFaceRecon !== 'undefined') {
                setFaceSdkReady(true);
                window.clearInterval(id);
            }
        }, 200);
        return () => window.clearInterval(id);
    }, [currentStep, faceSdkReady, setSignMetadata, confiaSignUrl]);

    const advanceCollection = useCallback(
        (data: any) => {
            const nextStep: string | null | undefined = data?.next_collection_step;
            if (!nextStep || nextStep === 'completing' || !isCollectionStep(nextStep)) {
                onCollectionComplete();
                return;
            }
            if (nextStep === 'face_recognition' && data?.provider === 'confia') {
                setConfiaSignUrl(data?.confia_sign_url);
            }
            setCurrentStep(nextStep);
        },
        [onCollectionComplete],
    );

    const submitStep = useCallback(
        async (step: string, payload: Record<string, unknown>) => {
            if (!folderSessionToken) return;

            setSubmitting(true);
            setSubmitError(null);

            try {
                const response = await axios.patch(
                    '/folder_signer/collection',
                    { step, ...payload },
                    buildAuthHeaders(folderSessionToken),
                );
                advanceCollection(response.data);
            } catch (error: any) {
                const status = error.response?.status;
                if (status === 401) {
                    setSessionExpired();
                    return;
                }
                if (status === 409) {
                    const refreshed = await refreshListing();
                    const refreshedStatus = refreshed?.status;
                    if (refreshedStatus === 'completing' || refreshedStatus === 'completed') {
                        onCollectionComplete();
                    } else {
                        setSubmitError('Não foi possível avançar. Verifique o status da operação e tente novamente.');
                    }
                    return;
                }
                setSubmitError('Ocorreu um erro inesperado. Por favor, tente novamente.');
            } finally {
                setSubmitting(false);
            }
        },
        [folderSessionToken, buildAuthHeaders, refreshListing, setSessionExpired, onCollectionComplete, advanceCollection],
    );

    const getFaceToken = useCallback(async (): Promise<string | null> => {
        if (!folderSessionToken) return null;
        try {
            const response = await axios.get(
                '/folder_signer/liveness_client_session_key',
                buildAuthHeaders(folderSessionToken),
            );
            return response.data?.client_session_key ?? null;
        } catch (error: any) {
            if (error.response?.status === 401) setSessionExpired();
            return null;
        }
    }, [folderSessionToken, buildAuthHeaders, setSessionExpired]);

    const submitFace = useCallback(
        async (imageKey: string) => {
            setFaceImageKey(imageKey);
            await submitStep('face_recognition', {
                image_key: imageKey,
                event_date: new Date().toISOString(),
            });
        },
        [submitStep],
    );

    const pollConfiaFaceAndAdvance = useCallback(async (): Promise<number> => {
        if (!folderSessionToken) return 0;
        try {
            const response = await axios.patch(
                '/folder_signer/collection',
                {
                    step: 'face_recognition',
                    image_key: 'generated_by_confia',
                    event_date: new Date().toISOString(),
                },
                buildAuthHeaders(folderSessionToken),
            );
            advanceCollection(response.data);
            return 0;
        } catch (error: any) {
            const status = error.response?.status;
            if (status === 403) return 0;
            if (status === 401) {
                setSessionExpired();
                return 0;
            }
            if (status === 409) {
                const refreshed = await refreshListing();
                const refreshedStatus = refreshed?.status;
                if (refreshedStatus === 'completing' || refreshedStatus === 'completed') {
                    onCollectionComplete();
                    return 0;
                }
                return 409;
            }
            return 500;
        }
    }, [folderSessionToken, buildAuthHeaders, advanceCollection, refreshListing, setSessionExpired, onCollectionComplete]);

    const sendCollectionOtp = useCallback(() => {
        if (!folderSessionToken) return;
        axios
            .post('/folder_signer/authentication', {}, buildAuthHeaders(folderSessionToken))
            .catch((error: any) => {
                if (error.response?.status === 401) setSessionExpired();
            });
    }, [folderSessionToken, buildAuthHeaders, setSessionExpired]);

    const submitCollectionOtp = useCallback(
        async (code: string) => {
            if (!folderSessionToken) return;

            let response;
            try {
                response = await axios.patch(
                    '/folder_signer/collection',
                    { step: 'authentication_code', authentication_code: code },
                    buildAuthHeaders(folderSessionToken),
                );
            } catch (error: any) {
                const status = error.response?.status;
                if (status === 401) {
                    setSessionExpired();
                    return;
                }
                if (status === 409) {
                    const refreshed = await refreshListing();
                    const refreshedStatus = refreshed?.status;
                    if (refreshedStatus === 'completing' || refreshedStatus === 'completed') {
                        onCollectionComplete();
                    } else {
                        setSubmitError('Não foi possível avançar. Verifique o status da operação e tente novamente.');
                    }
                    return;
                }
                throw error;
            }
            advanceCollection(response.data);
        },
        [folderSessionToken, buildAuthHeaders, refreshListing, setSessionExpired, onCollectionComplete, advanceCollection],
    );

    if (submitting) {
        return <PrivatePayrollAuctionLoading />;
    }

    const renderStep = () => {
        if (currentStep === 'face_recognition') {
            if (confiaSignUrl) {
                return (
                    <ConfiaPage
                        signData={{}}
                        setSignData={() => undefined}
                        setSignerStep={(step: string) => {
                            if (step === 'login') setSessionExpired();
                        }}
                        folderMode={{
                            confiaSignUrl,
                            pollAndAdvance: pollConfiaFaceAndAdvance,
                        }}
                    />
                );
            }
            if (!faceSdkReady) {
                return <PrivatePayrollAuctionLoading />;
            }
            const signerData = listing?.signer_data ?? listing?.signer_batch_data ?? {};
            const base = window.location.href.split('?')[0];
            const params = new URLSearchParams();
            if (signerData?.document_number) params.set('document_number', signerData.document_number);
            if (signerData?.birthdate) {
                params.set('birthdate', String(signerData.birthdate).split('-').reverse().join('-'));
            }
            if (signerData?.name) params.set('name', signerData.name);
            const qs = params.toString();
            const desktopQrUrl = qs ? `${base}?${qs}` : base;

            return (
                <PrivatePayrollAuctionTheme.FaceRecognition
                    signData={{}}
                    setSignerStep={(step: string) => {
                        if (step === 'login') setSessionExpired();
                    }}
                    setSignData={() => undefined}
                    setAlreadySigned={() => undefined}
                    setCanceled={() => undefined}
                    setExpired={() => undefined}
                    signerHash={listing?.folder_hash ?? ''}
                    folderMode={{ getToken: getFaceToken, submit: submitFace, desktopQrUrl }}
                />
            );
        }

        if (currentStep === 'personal_document') {
            return (
                <PrivatePayrollAuctionTheme.PersonalDocument
                    signData={{
                        document_templates: listing?.document_templates,
                        ocr_token: listing?.ocr_token,
                        image_key: faceImageKey,
                        signer_data: listing?.signer_data ?? listing?.signer_batch_data ?? {},
                    }}
                    setSignerStep={(step: string) => {
                        if (step === 'login') setSessionExpired();
                    }}
                    setSignData={() => undefined}
                    signerHash={listing?.folder_hash ?? ''}
                    folderMode={{
                        submitOcr: (payload) => submitStep('personal_document', payload),
                    }}
                />
            );
        }

        if (currentStep === 'authentication_code') {
            return (
                <PrivatePayrollAuctionTheme.OTP
                    signData={{}}
                    setSignData={() => undefined}
                    setSignerStep={(step: string) => {
                        if (step === 'login') setSessionExpired();
                    }}
                    onSendOTPCode={() => undefined}
                    folderMode={{
                        signerData: listing?.signer_data ?? listing?.signer_batch_data ?? {},
                        sendCode: sendCollectionOtp,
                        submitCode: submitCollectionOtp,
                    }}
                />
            );
        }

        return null;
    };

    if (submitError) {
        return (
            <S.PageWrapper>
                <S.LogoSection>
                    {theme.logo_url && <S.LogoImage src={theme.logo_url} alt="logo" />}
                </S.LogoSection>
                <S.Content>
                    <S.ErrorCard>
                        <S.ErrorText>{submitError}</S.ErrorText>
                        <ThemedButton
                            clientThemeSettings={theme}
                            onClick={() => setSubmitError(null)}
                        >
                            Tentar novamente
                        </ThemedButton>
                    </S.ErrorCard>
                </S.Content>
                <S.Footer>
                    <PoweredByQITech />
                </S.Footer>
            </S.PageWrapper>
        );
    }

    return <>{renderStep()}</>;
};
